import { useEffect, useRef } from 'react';
import { useGetStoreInfo } from 'domains/Auth/hooks';
import { getOffer, hasFeature } from './lendingService';
import { setWindowAttr } from './setWindowAttr.helper';

const scriptId = 'lending-awareness-script';
const hasScript = () => !!document.getElementById(scriptId);

const scriptUrl = `${
  import.meta.env.VITE_LENDING_AWARENESS_BUILD_JS
}?${new Date().getTime()}`;

type InjectLendingAwarenessProps = {
  app: string;
  variant?: 'blue' | 'white';
};

export function LendingAwareness({
  app,
  variant = 'blue',
}: InjectLendingAwarenessProps) {
  const storeInfo = useGetStoreInfo();
  const isMountedRef = useRef(true);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (hasScript() || loadedRef.current) return;

    const toggle = `awareness_banner_${app}`;
    const script = document.createElement('script');

    const loadData = async () => {
      try {
        loadedRef.current = true;

        if (!isMountedRef.current) return;

        const [enabled, offer] = await Promise.all([
          hasFeature(toggle),
          getOffer(),
        ]);

        const toggles: Record<string, boolean> = { [toggle]: enabled };
        setWindowAttr('Lending', { app, storeInfo, offer, toggles, variant });

        script.id = scriptId;
        script.src = scriptUrl;
        document.body.appendChild(script);
      } catch (error) {
        loadedRef.current = false;
        console.error('[AWARENESS]', error);
      }
    };

    void loadData();

    return () => {
      isMountedRef.current = false;
      const scriptEl = document.getElementById(scriptId);
      if (scriptEl) {
        document.body.removeChild(scriptEl);
      }
    };
  }, [app, storeInfo, variant]);

  return <div id="root-lending-awareness" />;
}
